import { FunctionComponent } from 'react';
import { SimpleObject } from '../../types';

const statusClass = (estado: string) => {
  if (estado === 'Finalizada') return 'finalizada';
  if (estado === 'En progreso') return 'en-progreso';
  if (estado === 'En Espera') return 'en-espera';
  if (estado === 'Error') return 'error';
  return '';
};

export interface EstudioConexionResumenProyectoCardProps {
  estudio: SimpleObject;
  /** En resultados base se muestra chip de estado; en /mitigaciones no (D-CFG-12). */
  showEstadoSimulacion?: boolean;
  /** En resultado-mitigacion el estado principal es mitigación; base queda secundario. */
  showEstadoMitigacion?: boolean;
}

/**
 * Tarjeta "Resumen del Estudio": proyecto, red, conexión, GD, batería.
 */
const EstudioConexionResumenProyectoCard: FunctionComponent<EstudioConexionResumenProyectoCardProps> = ({
  estudio,
  showEstadoSimulacion = false,
  showEstadoMitigacion = false,
}) => {
  const estadoTexto = String(estudio.estadoSimulacionTexto || estudio.estado || '—');
  const mitEstadoTexto = String(estudio.estadoMitigacionTexto || estudio.estadoMitigacion || '—');
  const tieneBateria = Boolean(estudio.bateriaIncluida);

  const tensionValor =
    estudio.tensionConexion != null
      ? `${estudio.tensionConexion} kV (${estudio.nivelTension === 'bt' ? 'BT' : 'MT'})`
      : estudio.nivelTension === 'bt'
        ? 'BT'
        : estudio.nivelTension === 'mt'
          ? 'MT'
          : '—';

  const coordenadas =
    estudio.latitud != null && estudio.longitud != null
      ? `${Number(estudio.latitud).toFixed(6)}, ${Number(estudio.longitud).toFixed(6)}`
      : '—';

  return (
    <div className='ecg-section vdr-ecg-summary'>
      <h3 className='ecg-section-title'>Resumen del Estudio</h3>
      <div className='ecg-info-card'>

        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Empresa</span>
          <span className='ecg-info-value'>{estudio.empresaNombre ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Subestación</span>
          <span className='ecg-info-value'>{estudio.subestacionNombre ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Alimentador</span>
          <span className='ecg-info-value'>{estudio.alimentadorNombre ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Tipo GD</span>
          <span className='ecg-info-value'>{estudio.tipoGd ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Potencia</span>
          <span className='ecg-info-value'>
            {estudio.potenciaKw != null ? `${estudio.potenciaKw} kW` : '—'}
          </span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Factor de Potencia</span>
          <span className='ecg-info-value'>
            {estudio.factorPotencia != null
              ? `${estudio.factorPotencia} ${estudio.factorPotenciaNaturaleza || 'capacitivo'}`
              : '—'}
          </span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Densidad</span>
          <span className='ecg-info-value'>{estudio.densidad ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Tensión de conexión</span>
          <span className='ecg-info-value'>{tensionValor}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Poste</span>
          <span className='ecg-info-value'>{estudio.posteId ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Nodo de conexión</span>
          <span className='ecg-info-value'>{estudio.nodoConexion ?? '—'}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Coordenadas</span>
          <span className='ecg-info-value'>{coordenadas}</span>
        </div>

        {/* Batería: un único ítem cuando no aplica */}
        {tieneBateria ? (
          <>
            <div className='ecg-info-item'>
              <span className='ecg-info-label'>Batería — potencia</span>
              <span className='ecg-info-value'>
                {estudio.bateria?.potenciaKw != null ? `${estudio.bateria.potenciaKw} kW` : '—'}
              </span>
            </div>
            <div className='ecg-info-item'>
              <span className='ecg-info-label'>Batería — energía</span>
              <span className='ecg-info-value'>
                {estudio.bateria?.energiaKwh != null
                  ? `${estudio.bateria.energiaKwh} kWh`
                  : '—'}
              </span>
            </div>
            <div className='ecg-info-item'>
              <span className='ecg-info-label'>Bloque carga batería</span>
              <span className='ecg-info-value'>{estudio.bateria?.bloqueCarga ?? '—'}</span>
            </div>
            <div className='ecg-info-item'>
              <span className='ecg-info-label'>Bloque descarga batería</span>
              <span className='ecg-info-value'>{estudio.bateria?.bloqueDescarga ?? '—'}</span>
            </div>
          </>
        ) : (
          <div className='ecg-info-item'>
            <span className='ecg-info-label'>Batería</span>
            <span className='ecg-info-value'>No aplica</span>
          </div>
        )}

        {/* Estados — siempre como chips coloreados */}
        {showEstadoSimulacion && !showEstadoMitigacion && (
          <div className='ecg-info-item'>
            <span className='ecg-info-label'>Estado base</span>
            <span className={`ecg-status-chip ${statusClass(estadoTexto)}`}>{estadoTexto}</span>
          </div>
        )}
        {showEstadoMitigacion && (
          <>
            <div className='ecg-info-item'>
              <span className='ecg-info-label'>Estado mitigación</span>
              <span className={`ecg-status-chip ${statusClass(mitEstadoTexto)}`}>
                {mitEstadoTexto}
              </span>
            </div>
            <div className='ecg-info-item'>
              <span className='ecg-info-label'>Estudio base</span>
              <span className={`ecg-status-chip ${statusClass('Finalizada')}`}>Finalizada</span>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default EstudioConexionResumenProyectoCard;
